import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Wallet } from "lucide-react";
import { useApi } from "../lib/api";
import { fmtNum, shortAddr } from "../lib/format";
import { SectionHead, DataBadge, Skel, Empty, ErrorState } from "../components/common";
import usePageMeta from "../lib/usePageMeta";

export default function Wallets() {
  usePageMeta("Top Accounts", "Top Robinhood Chain accounts by balance and live wallet explorer.");
  const [addr, setAddr] = useState("");
  const nav = useNavigate();
  const { data } = useApi("/accounts/top");
  const { data: stats } = useApi("/explorer/stats");
  const ethPrice = parseFloat(stats?.data?.coin_price || 0);

  const go = (e) => {
    e.preventDefault();
    const a = addr.trim();
    if (/^0x[a-fA-F0-9]{40}$/.test(a)) nav(`/wallet/${a}`);
  };

  return (
    <div className="space-y-5">
      <SectionHead title="Wallet Explorer" badge={<DataBadge live={data?.live ?? true} />} />
      <div className="t-card p-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-3 text-[#9CA3AF]"><Wallet size={16} /><span className="text-sm">Inspect any Robinhood Chain wallet — live balances, token holdings, transactions and transfers.</span></div>
        <form onSubmit={go} className="flex gap-2">
          <input data-testid="wallet-search-input" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="0x wallet address…"
            className="flex-1 bg-[#0A0A0A] border border-[#262626] rounded-sm px-3 py-2 num text-[13px] outline-none focus:border-[#059669]" />
          <button data-testid="wallet-search-button" type="submit" disabled={!/^0x[a-fA-F0-9]{40}$/.test(addr.trim())}
            className="px-4 py-2 bg-[#059669] hover:bg-[#047857] disabled:opacity-40 text-white text-[13px] rounded-sm transition-colors flex items-center gap-2"><Search size={14} /> Analyze</button>
        </form>
      </div>

      <SectionHead title="Top Accounts by Balance" />
      {data && !data.live && <ErrorState />}
      <div className="t-card overflow-x-auto">
        {!data ? <Skel rows={10} h={34} /> : !data.items.length ? <Empty /> : (
          <table className="w-full t-table" data-testid="top-accounts-table">
            <thead><tr className="text-left border-b border-[#262626]"><th>#</th><th>Address</th><th>Label</th><th className="text-right">ETH Balance</th><th className="text-right">USD Value</th><th className="text-right">Txs</th></tr></thead>
            <tbody>
              {data.items.map((a, i) => {
                const bal = parseInt(a.coin_balance || "0") / 1e18;
                return (
                  <tr key={a.hash} onClick={() => nav(`/wallet/${a.hash}`)} data-testid={`account-row-${i}`}>
                    <td className="num text-[#6B7280]">{i + 1}</td>
                    <td className="num text-[13px] text-[#10B981]">{shortAddr(a.hash, 8)}</td>
                    <td className="text-xs text-[#9CA3AF]">{a.name || (a.is_contract ? "Contract" : "—")}</td>
                    <td className="text-right num">{bal.toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                    <td className="text-right num text-[#9CA3AF]">{ethPrice ? `$${(bal * ethPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}</td>
                    <td className="text-right num text-[#6B7280]">{fmtNum(parseInt(a.transactions_count || a.transaction_count || 0))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
